import { injectable } from 'inversify';
import type { Point } from 'diagram-js/lib/util/Types';

import type {
  DraggingReturnConnection,
  FlowSchema,
  ResolvedTargetShape,
  SelectedNode,
  ShapeLikeElement,
} from '../../types/event-types';

const NODE_MIDDLE_DIVIDER = 2;
const GROUP_TARGET_DATA_ATTRIBUTE = 'data-flow-group-id';
const NODE_TARGET_DATA_ATTRIBUTE = 'data-flow-node-id';

@injectable()
export class EventTypesCanvasInteractionService {
  getEligibleReturnIds(
    schema: FlowSchema,
    sourceId: string,
    isReturnNode: (
      node: FlowSchema['nodes'][number] | null | undefined,
    ) => boolean,
    isReturnTargetAllowed: (
      schema: FlowSchema,
      sourceNodeId: string,
      targetNode: SelectedNode,
    ) => boolean,
  ): Set<string> {
    const eligibleIds = new Set(
      schema.nodes
        .filter((node) =>
          isReturnTargetAllowed(schema, sourceId, {
            id: node.id,
            type: node.type,
          }),
        )
        .map((node) => node.id),
    );
    const returnableGroupIds = new Set<string>();

    for (const node of schema.nodes) {
      if (
        node.parallelGroupId &&
        isReturnNode(node) &&
        isReturnTargetAllowed(schema, sourceId, {
          id: node.parallelGroupId,
          type: 'ParallelGroup',
        })
      ) {
        returnableGroupIds.add(node.parallelGroupId);
      }
    }

    returnableGroupIds.forEach((groupId) => eligibleIds.add(groupId));

    return eligibleIds;
  }

  getTargetSchemaIdFromDom(targetElement: Element | null): string | null {
    const targetGroupElement = targetElement?.closest(
      `[${GROUP_TARGET_DATA_ATTRIBUTE}]`,
    ) as HTMLElement | null;

    if (targetGroupElement) {
      return targetGroupElement.getAttribute(GROUP_TARGET_DATA_ATTRIBUTE);
    }

    const targetNodeElement = targetElement?.closest(
      `[${NODE_TARGET_DATA_ATTRIBUTE}]`,
    ) as HTMLElement | null;

    return targetNodeElement?.getAttribute(NODE_TARGET_DATA_ATTRIBUTE) ?? null;
  }

  getTargetSchemaIdByShape(
    schemaIdToBpmnId: Map<string, string>,
    bpmnShapeId: string | undefined,
  ): string | null {
    if (!bpmnShapeId) {
      return null;
    }

    for (const [schemaId, bpmnId] of schemaIdToBpmnId.entries()) {
      if (bpmnId === bpmnShapeId) {
        return schemaId;
      }
    }

    return null;
  }

  getRayRectIntersection(
    from: Point,
    to: Point,
    rect: { x: number; y: number; width: number; height: number },
  ): Point {
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    const left = rect.x;
    const right = rect.x + rect.width;
    const top = rect.y;
    const bottom = rect.y + rect.height;

    let tMin = 0;
    let tMax = 1;

    const clip = (p: number, q: number) => {
      if (p === 0) return q >= 0;
      const t = q / p;
      if (p < 0) {
        if (t > tMax) return false;
        if (t > tMin) tMin = t;
      } else {
        if (t < tMin) return false;
        if (t < tMax) tMax = t;
      }
      return true;
    };

    if (
      clip(-dx, from.x - left) &&
      clip(dx, right - from.x) &&
      clip(-dy, from.y - top) &&
      clip(dy, bottom - from.y)
    ) {
      return {
        x: from.x + dx * tMin,
        y: from.y + dy * tMin,
      };
    }

    return to;
  }

  getShapeAtPoint(
    elements: ShapeLikeElement[],
    point: Point,
    excludeId?: string,
  ): ShapeLikeElement | null {
    for (let index = elements.length - 1; index >= 0; index--) {
      const element = elements[index];

      if (element.id === excludeId) {
        continue;
      }

      if (
        typeof element.x !== 'number' ||
        typeof element.y !== 'number' ||
        typeof element.width !== 'number' ||
        typeof element.height !== 'number'
      ) {
        continue;
      }

      const withinX =
        point.x >= element.x && point.x <= element.x + element.width;
      const withinY =
        point.y >= element.y && point.y <= element.y + element.height;

      if (withinX && withinY) {
        return element;
      }
    }

    return null;
  }

  resolveDraggingReturnTarget(params: {
    sourceId: string;
    targetElement: Element | null;
    shapeElements: ShapeLikeElement[];
    cursorPoint: Point;
    schemaIdToBpmnId: Map<string, string>;
    eligibleReturnIds: Set<string>;
  }): {
    rawTargetId: string | null;
    targetId: string | null;
    targetShape: ResolvedTargetShape | null;
  } {
    const {
      sourceId,
      targetElement,
      shapeElements,
      cursorPoint,
      schemaIdToBpmnId,
      eligibleReturnIds,
    } = params;
    const hoveredTargetId = this.getTargetSchemaIdFromDom(targetElement);
    const targetShapeCandidate = this.getShapeAtPoint(
      shapeElements,
      cursorPoint,
      schemaIdToBpmnId.get(sourceId),
    );
    const targetIdByShape = this.getTargetSchemaIdByShape(
      schemaIdToBpmnId,
      targetShapeCandidate?.id,
    );
    const rawTargetId = hoveredTargetId ?? targetIdByShape ?? null;
    const targetId =
      rawTargetId &&
      rawTargetId !== sourceId &&
      eligibleReturnIds.has(rawTargetId)
        ? rawTargetId
        : null;
    const targetShape = targetId
      ? this.getShapeBySchemaIdFromElements(
          shapeElements,
          schemaIdToBpmnId,
          targetId,
        )
      : null;

    return {
      rawTargetId,
      targetId,
      targetShape,
    };
  }

  private getShapeBySchemaIdFromElements(
    shapeElements: ShapeLikeElement[],
    schemaIdToBpmnId: Map<string, string>,
    targetId: string,
  ): ResolvedTargetShape | null {
    const bpmnId = schemaIdToBpmnId.get(targetId);

    if (!bpmnId) {
      return null;
    }

    const shapeElement =
      shapeElements.find((candidate) => candidate.id === bpmnId) ?? null;

    if (
      !shapeElement ||
      typeof shapeElement.x !== 'number' ||
      typeof shapeElement.y !== 'number' ||
      typeof shapeElement.width !== 'number' ||
      typeof shapeElement.height !== 'number'
    ) {
      return null;
    }

    return {
      id: shapeElement.id,
      x: shapeElement.x,
      y: shapeElement.y,
      width: shapeElement.width,
      height: shapeElement.height,
    };
  }

  buildDraggingReturnConnection(params: {
    previousConnection: DraggingReturnConnection;
    sourceId: string;
    sourceShape: ShapeLikeElement;
    targetElement: Element | null;
    shapeElements: ShapeLikeElement[];
    schemaIdToBpmnId: Map<string, string>;
    eligibleReturnIds: Set<string>;
    cursorPoint: Point;
    viewbox: {
      x: number;
      y: number;
      scale: number;
    };
  }): DraggingReturnConnection {
    const {
      previousConnection,
      sourceId,
      sourceShape,
      targetElement,
      shapeElements,
      schemaIdToBpmnId,
      eligibleReturnIds,
      cursorPoint,
      viewbox,
    } = params;
    const { targetId, targetShape } = this.resolveDraggingReturnTarget({
      sourceId,
      targetElement,
      shapeElements,
      cursorPoint,
      schemaIdToBpmnId,
      eligibleReturnIds,
    });

    let nextSourceSide = previousConnection.sourceSide;

    if (typeof sourceShape.x === 'number' && cursorPoint.x <= sourceShape.x) {
      nextSourceSide = 'left';
    }

    if (
      typeof sourceShape.x === 'number' &&
      typeof sourceShape.width === 'number' &&
      cursorPoint.x >= sourceShape.x + sourceShape.width
    ) {
      nextSourceSide = 'right';
    }

    const sourcePoint =
      nextSourceSide === 'right'
        ? {
            x: (sourceShape.x ?? 0) + (sourceShape.width ?? 0),
            y:
              (sourceShape.y ?? 0) +
              (sourceShape.height ?? 0) / NODE_MIDDLE_DIVIDER,
          }
        : {
            x: sourceShape.x ?? 0,
            y:
              (sourceShape.y ?? 0) +
              (sourceShape.height ?? 0) / NODE_MIDDLE_DIVIDER,
          };
    const snappedCursorPoint = targetShape
      ? this.getRayRectIntersection(sourcePoint, cursorPoint, targetShape)
      : cursorPoint;

    return {
      ...previousConnection,
      waypoints: [sourcePoint, snappedCursorPoint],
      viewbox,
      isSnappedToNode: !!targetShape,
      targetNodeId: targetId,
      sourceSide: nextSourceSide,
    };
  }
}
